/**
 * First-admin onboarding wizard (Section 8A).
 *
 * Linear 7-step guided setup that walks a brand-new org owner from a
 * blank workspace to first useful screen. Progress is persisted server-
 * side after every step via ``POST /onboarding/step`` so an admin can
 * close the tab and resume where they left off. The ``finish`` step
 * flips ``organization.onboarding_completed_at`` and redirects to the
 * dashboard.
 *
 * Each step edits real data using the same APIs the rest of the app
 * uses — nothing here is wizard-local. Dropping the wizard still leaves
 * a consistent workspace.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Plus,
  X,
  Building2,
  Users,
  Upload,
  FileText,
  CalendarDays,
  Plug,
  Mail,
  PartyPopper,
} from "lucide-react";
import { Card, Button, Input } from "@/components/ui";
import { useToast } from "@/hooks/useToast";
import {
  getOnboardingState,
  markOnboardingStep,
  completeOnboarding,
  type OnboardingState,
  type WizardStep,
} from "@/api/signup";
import {
  getCurrentOrg,
  listDepartments,
  createDepartment,
  updateOrg,
} from "@/api/organizations";
import type { Organization, DepartmentFull } from "@/types/organization";

// ─────────────────────────── step definitions ───────────────────────────

interface StepSpec {
  key: WizardStep;
  title: string;
  subtitle: string;
  icon: typeof Building2;
}

const STEPS: StepSpec[] = [
  {
    key: "company_profile",
    title: "Company Profile",
    subtitle: "A few details so invitations and documents look polished.",
    icon: Building2,
  },
  {
    key: "departments",
    title: "Departments",
    subtitle: "Group employees by the teams they actually work on.",
    icon: Users,
  },
  {
    key: "import_employees",
    title: "Import Employees",
    subtitle: "Bring your team over — CSV, manual, or just yourself for now.",
    icon: Upload,
  },
  {
    key: "document_templates",
    title: "Document Templates",
    subtitle: "Pre-loaded NDAs, handbooks, and offer letters. Toggle what you use.",
    icon: FileText,
  },
  {
    key: "leave_policy",
    title: "Leave Policy",
    subtitle: "PTO, sick leave, holidays — configure once, applies to everyone.",
    icon: CalendarDays,
  },
  {
    key: "integrations",
    title: "Integrations",
    subtitle: "Connect Slack, Google Calendar, payroll. Skip anything you don't need.",
    icon: Plug,
  },
  {
    key: "invite_team",
    title: "Invite Your Team",
    subtitle: "Send them an email — they can start exploring right away.",
    icon: Mail,
  },
];

const SUGGESTED_DEPARTMENTS = [
  "Engineering",
  "Sales",
  "Marketing",
  "HR",
  "Finance",
  "Operations",
  "Product",
  "Design",
  "Legal",
  "Support",
];

const INDUSTRIES = [
  "Technology",
  "Financial Services",
  "Healthcare",
  "Retail",
  "Manufacturing",
  "Media",
  "Education",
  "Non-Profit",
  "Other",
];

const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"];

// ─────────────────────────── component ───────────────────────────

export function OnboardingWizardPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [state, setState] = useState<OnboardingState | null>(null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [departments, setDepartments] = useState<DepartmentFull[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    Promise.all([getOnboardingState(), getCurrentOrg(), listDepartments()])
      .then(([s, o, d]) => {
        if (s.completed) {
          navigate("/", { replace: true });
          return;
        }
        setState(s);
        setOrg(o);
        setDepartments(d);
        // Jump to first uncompleted step.
        const firstIdx = STEPS.findIndex(
          (st) => !s.completed_steps.includes(st.key),
        );
        setStepIndex(firstIdx === -1 ? STEPS.length - 1 : firstIdx);
      })
      .catch(() => navigate("/", { replace: true }));
  }, [navigate]);

  const markDone = useCallback(
    async (key: WizardStep) => {
      try {
        const next = await markOnboardingStep(key);
        setState(next);
      } catch {
        // Non-fatal — user can retry on the next step.
      }
    },
    [],
  );

  const goNext = async (key: WizardStep) => {
    setSaving(true);
    await markDone(key);
    setSaving(false);
    if (stepIndex + 1 < STEPS.length) {
      setStepIndex(stepIndex + 1);
    }
  };

  const goPrev = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const finishWizard = async () => {
    setFinishing(true);
    try {
      await markOnboardingStep("finish");
      addToast("success", "Welcome to Lenavio! 🎉");
      navigate("/", { replace: true });
    } catch {
      addToast("error", "Could not finalize setup");
      setFinishing(false);
    }
  };

  const handleSkipAll = async () => {
    if (!confirm("Skip the rest of setup? You can re-run it anytime from Settings.")) return;
    setFinishing(true);
    try {
      await completeOnboarding();
      navigate("/", { replace: true });
    } finally {
      setFinishing(false);
    }
  };

  if (!state || !org) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
      </div>
    );
  }

  const currentStep = STEPS[stepIndex];
  const completed = new Set(state.completed_steps);
  const progressPct = (completed.size / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-background py-10 px-6">
      <div className="max-w-3xl mx-auto">
        <header className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold font-heading text-text-primary">
              Let's set up {org.name}
            </h1>
            <p className="text-text-muted mt-1">
              Step {stepIndex + 1} of {STEPS.length}
              {state.trial_days_remaining !== null &&
                state.trial_days_remaining >= 0 &&
                ` — ${state.trial_days_remaining} days left in trial`}
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={handleSkipAll}
            disabled={finishing}
          >
            Skip for now
          </Button>
        </header>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="h-1.5 w-full rounded-full bg-surface overflow-hidden">
            <motion.div
              className="h-full bg-accent"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-text-muted">
            {STEPS.map((s, i) => {
              const isDone = completed.has(s.key);
              const isCurrent = i === stepIndex;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setStepIndex(i)}
                  className={`flex flex-col items-center gap-1 transition-colors ${
                    isCurrent
                      ? "text-accent"
                      : isDone
                      ? "text-text-primary"
                      : "text-text-muted"
                  }`}
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                      isDone
                        ? "bg-green-500 text-white"
                        : isCurrent
                        ? "bg-accent text-white"
                        : "bg-surface border border-border"
                    }`}
                  >
                    {isDone ? <Check className="w-3 h-3" /> : i + 1}
                  </span>
                  <span className="hidden sm:block text-[10px] max-w-[64px] text-center leading-tight">
                    {s.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active step */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep.key}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="p-6">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center">
                  <currentStep.icon className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-text-primary">
                    {currentStep.title}
                  </h2>
                  <p className="text-sm text-text-muted mt-0.5">
                    {currentStep.subtitle}
                  </p>
                </div>
              </div>

              {currentStep.key === "company_profile" && (
                <CompanyProfileStep
                  org={org}
                  onSaved={(o) => setOrg(o)}
                />
              )}
              {currentStep.key === "departments" && (
                <DepartmentsStep
                  departments={departments}
                  onCreated={(d) => setDepartments((p) => [...p, d])}
                />
              )}
              {currentStep.key === "import_employees" && (
                <ImportEmployeesStep onSkip={() => goNext("import_employees")} />
              )}
              {currentStep.key === "document_templates" && <DocTemplatesStep />}
              {currentStep.key === "leave_policy" && <LeavePolicyStep />}
              {currentStep.key === "integrations" && <IntegrationsStep />}
              {currentStep.key === "invite_team" && <InviteTeamStep />}
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Footer nav */}
        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={goPrev}
            disabled={stepIndex === 0 || saving || finishing}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>

          {stepIndex < STEPS.length - 1 ? (
            <Button
              onClick={() => goNext(currentStep.key)}
              disabled={saving || finishing}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {completed.has(currentStep.key) ? "Next" : "Save & continue"}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          ) : (
            <Button onClick={finishWizard} disabled={finishing}>
              {finishing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <PartyPopper className="w-4 h-4" />
                  Finish setup
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── step components ───────────────────────────

function CompanyProfileStep({
  org,
  onSaved,
}: {
  org: Organization;
  onSaved: (o: Organization) => void;
}) {
  const { addToast } = useToast();
  const [name, setName] = useState(org.name);
  const [address, setAddress] = useState(org.address ?? "");
  const [industry, setIndustry] = useState(
    (org.settings_json?.industry as string) ?? "",
  );
  const [size, setSize] = useState(
    (org.settings_json?.company_size as string) ?? "",
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateOrg({
        name,
        address: address || null,
        settings_json: {
          ...(org.settings_json || {}),
          industry,
          company_size: size,
        },
      });
      onSaved(updated);
      addToast("success", "Company profile saved");
    } catch {
      addToast("error", "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Company name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div>
          <label className="mb-1 block text-xs font-medium">Industry</label>
          <select
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
          >
            <option value="">Select…</option>
            {INDUSTRIES.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Company size</label>
          <select
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={size}
            onChange={(e) => setSize(e.target.value)}
          >
            <option value="">Select…</option>
            {COMPANY_SIZES.map((s) => (
              <option key={s} value={s}>
                {s} employees
              </option>
            ))}
          </select>
        </div>
        <Input
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="123 Market St, San Francisco"
        />
      </div>
      <p className="text-xs text-text-muted">
        Logo upload is available in Settings → General once you've finished setup.
      </p>
      <Button variant="secondary" size="sm" onClick={save} disabled={saving}>
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save changes"}
      </Button>
    </div>
  );
}

function DepartmentsStep({
  departments,
  onCreated,
}: {
  departments: DepartmentFull[];
  onCreated: (d: DepartmentFull) => void;
}) {
  const { addToast } = useToast();
  const [input, setInput] = useState("");
  const [creating, setCreating] = useState(false);

  const existingNames = useMemo(
    () => new Set(departments.map((d) => d.name.toLowerCase())),
    [departments],
  );

  const create = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || existingNames.has(trimmed.toLowerCase())) return;
    setCreating(true);
    try {
      const d = await createDepartment({ name: trimmed });
      onCreated(d);
      setInput("");
    } catch {
      addToast("error", `Could not create ${trimmed}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void create(input);
        }}
        className="flex gap-2"
      >
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Add a department, e.g. Engineering"
          className="flex-1"
        />
        <Button type="submit" disabled={creating || !input.trim()}>
          <Plus className="w-4 h-4" />
          Add
        </Button>
      </form>

      {departments.length > 0 && (
        <div>
          <p className="text-xs text-text-muted mb-2">Your departments</p>
          <div className="flex flex-wrap gap-2">
            {departments.map((d) => (
              <span
                key={d.id}
                className="inline-flex items-center gap-1 rounded-full bg-accent/10 text-accent px-3 py-1 text-xs font-medium"
              >
                <Check className="w-3 h-3" />
                {d.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-xs text-text-muted mb-2">Suggested — tap to add</p>
        <div className="flex flex-wrap gap-2">
          {SUGGESTED_DEPARTMENTS.filter(
            (s) => !existingNames.has(s.toLowerCase()),
          ).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => void create(s)}
              disabled={creating}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-3 py-1 text-xs hover:bg-background transition-colors"
            >
              <Plus className="w-3 h-3" />
              {s}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ImportEmployeesStep({ onSkip }: { onSkip: () => void }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-text-muted">
        You've got three ways to bring your team in. Pick whichever fits —
        the importer and People page open in a new tab so you can keep the
        wizard running here.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <OptionCard
          title="Upload CSV"
          body="Bulk-create 10+ employees from a spreadsheet."
          href="/employees/import"
          cta="Open importer"
        />
        <OptionCard
          title="Add manually"
          body="Create a few employees one at a time from the People page."
          href="/employees"
          cta="Go to People"
        />
        <OptionCard
          title="Just me for now"
          body="You're the only one here. Invite the rest later."
          cta="Skip this step"
          onClick={onSkip}
        />
      </div>
      <p className="text-xs text-text-muted">
        You can import employees from <strong>Settings → People</strong> at
        any time. When you finish (or skip) this step, you'll come right
        back to the wizard.
      </p>
    </div>
  );
}

function DocTemplatesStep() {
  return (
    <div className="space-y-3">
      <p className="text-sm text-text-muted">
        Lenavio ships with starter templates (NDA, Handbook, Offer Letter).
        You can toggle them on/off or add your own from Settings → Document
        Templates.
      </p>
      <OptionCard
        title="Manage document templates"
        body="Review what's pre-loaded and upload custom files."
        href="/settings"
        cta="Open templates"
      />
    </div>
  );
}

function LeavePolicyStep() {
  return (
    <div className="space-y-3">
      <p className="text-sm text-text-muted">
        Set PTO days, sick days, your holiday calendar, and whether leave
        accrues monthly or is granted up-front.
      </p>
      <OptionCard
        title="Configure leave policy"
        body="Applies to every employee unless you override per-person."
        href="/settings"
        cta="Open leave policy"
      />
    </div>
  );
}

function IntegrationsStep() {
  return (
    <div className="space-y-3">
      <p className="text-sm text-text-muted">
        Connect the tools your team already uses. All of these are optional.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <OptionCard
          title="Slack"
          body="Post announcements and birthday reminders into a channel."
          href="/settings"
          cta="Connect Slack"
        />
        <OptionCard
          title="Google Calendar"
          body="Sync PTO blocks and 1:1s into your calendar."
          href="/settings"
          cta="Connect Google"
        />
      </div>
    </div>
  );
}

function InviteTeamStep() {
  return (
    <div className="space-y-3">
      <p className="text-sm text-text-muted">
        Invitations are sent from the People page — pick the teammates you
        want to onboard and we'll email them a link to set up their account.
      </p>
      <OptionCard
        title="Send invitations"
        body="Each invitee gets a branded email and can accept immediately."
        href="/employees"
        cta="Open People"
      />
      <p className="text-xs text-text-muted">
        You can always invite more people later from any employee's profile.
      </p>
    </div>
  );
}

function OptionCard({
  title,
  body,
  href,
  cta,
  onClick,
}: {
  title: string;
  body: string;
  href?: string;
  cta: string;
  /** If set (and no href), clicking anywhere on the card fires this
   *  instead — used by the "Skip this step" option so it advances the
   *  wizard rather than just being a decorative button. */
  onClick?: () => void;
}) {
  const content = (
    <div className="rounded-lg border border-border bg-surface p-4 h-full flex flex-col">
      <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
      <p className="text-xs text-text-muted mt-1 flex-1">{body}</p>
      <div className="mt-3">
        <Button variant="secondary" size="sm" className="w-full">
          {cta}
          {href ? <ArrowRight className="w-3 h-3" /> : <X className="w-3 h-3" />}
        </Button>
      </div>
    </div>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer">
        {content}
      </a>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-lg"
      >
        {content}
      </button>
    );
  }
  return content;
}
